import { exhaustiveSwitchError } from './exhaustiveSwitchError'

export enum ErrorCode {
  INTEGRATION_INSTANCE_HAS_LEASES = 'INTEGRATION_INSTANCE_HAS_LEASES',
  PROPERTY_SYNC_IN_PROGRESS = 'PROPERTY_SYNC_IN_PROGRESS',
  UNIT_TYPE_UPDATE_IN_PROGRESS = 'UNIT_TYPE_UPDATE_IN_PROGRESS',
  INVALID_CLIENT_DATA = 'INVALID_CLIENT_DATA',
  PRICE_CHANGE_VIOLATES_MAX_MANUAL_PRICE_RATIO = 'PRICE_CHANGE_VIOLATES_MAX_MANUAL_PRICE_RATIO',
  MISSING_FIXED_RENT_AFFORDABLE_RENEWAL_BEHAVIOR = 'MISSING_FIXED_RENT_AFFORDABLE_RENEWAL_BEHAVIOR',
  INVALID_RENT_CONTROL_DATE_RANGE = 'INVALID_RENT_CONTROL_DATE_RANGE',
  OVERLAPPING_PORTFOLIO_PROPERTIES = 'OVERLAPPING_PORTFOLIO_PROPERTIES',
  CANNOT_SET_MARKET_RENT_ADJUSTMENT_FOR_UNIT_WITHOUT_MARKET_RENT = 'CANNOT_SET_MARKET_RENT_ADJUSTMENT_FOR_UNIT_WITHOUT_MARKET_RENT',
  COMBINE_PROPERTIES_OWNERSHIP_GROUP_MISMATCH = 'COMBINE_PROPERTIES_OWNERSHIP_GROUP_MISMATCH',
  DUPLICATE_OWNERSHIP_GROUP_NAME = 'DUPLICATE_OWNERSHIP_GROUP_NAME',
  OWNERSHIP_GROUPS_NOT_ENABLED = 'OWNERSHIP_GROUPS_NOT_ENABLED',
  LEASE_UP_INVALID_STABILIZED_OCCUPANCY = 'LEASE_UP_INVALID_STABILIZED_OCCUPANCY',
  LEASE_UP_INVALID_MONTHLY_GOAL_MONTH_OFFSET = 'LEASE_UP_INVALID_MONTHLY_GOAL_MONTH_OFFSET',
  LEASE_UP_INVALID_MONTHLY_GOAL_RATIO = 'LEASE_UP_INVALID_MONTHLY_GOAL_RATIO',
  LEASE_UP_DUPLICATE_MONTHLY_GOAL_MONTH_OFFSET = 'LEASE_UP_DUPLICATE_MONTHLY_GOAL_MONTH_OFFSET',
  LEASE_UP_MONTHLY_GOALS_NOT_MONOTONIC = 'LEASE_UP_MONTHLY_GOALS_NOT_MONOTONIC',
  LEASE_UP_PLAN_ALREADY_EXISTS = 'LEASE_UP_PLAN_ALREADY_EXISTS',
  LEASE_UP_PROPERTY_NOT_IN_LEASE_UP_MODE = 'LEASE_UP_PROPERTY_NOT_IN_LEASE_UP_MODE',
  LEASE_UP_PLAN_NOT_EDITABLE = 'LEASE_UP_PLAN_NOT_EDITABLE',
  LEASE_UP_TARGET_DATE_NOT_AFTER_PRELEASE = 'LEASE_UP_TARGET_DATE_NOT_AFTER_PRELEASE',
  LEASE_UP_MONTHLY_GOAL_OUT_OF_RANGE = 'LEASE_UP_MONTHLY_GOAL_OUT_OF_RANGE',
  LEASE_UP_INVALID_MINIMUM_LEASED_PERCENT = 'LEASE_UP_INVALID_MINIMUM_LEASED_PERCENT',
  LEASE_UP_TARGET_DATE_NOT_AFTER_PLAN_START = 'LEASE_UP_TARGET_DATE_NOT_AFTER_PLAN_START',
}

export function getClientFacingErrorMessage(errorCode: ErrorCode): string | null {
  switch (errorCode) {
    case ErrorCode.INTEGRATION_INSTANCE_HAS_LEASES:
      return 'There are leases and lease intervals associated with the integration instance you are trying to delete. Would you like to delete all such leases and lease intervals? WARNING: this operation cannot be undone.'
    case ErrorCode.PROPERTY_SYNC_IN_PROGRESS:
      return 'Sync for this property is currently in progress. Please try again in a few minutes.'
    case ErrorCode.INVALID_CLIENT_DATA:
      return 'Your client data is out-of-date. Please refresh the page and try again.'
    case ErrorCode.PRICE_CHANGE_VIOLATES_MAX_MANUAL_PRICE_RATIO:
      return 'Price change violates max manual price ratio. Please either make a smaller change, or change the setting in property settings.'
    case ErrorCode.MISSING_FIXED_RENT_AFFORDABLE_RENEWAL_BEHAVIOR:
      return 'Please go to firm settings to define renewal behavior for fixed-rent affordable units first.'
    case ErrorCode.UNIT_TYPE_UPDATE_IN_PROGRESS:
      return 'Unit type update is currently in progress. Please try again in a few minutes.'
    case ErrorCode.INVALID_RENT_CONTROL_DATE_RANGE:
      return 'Invalid rent control date range. Please check the date range and try again.'
    case ErrorCode.OVERLAPPING_PORTFOLIO_PROPERTIES:
      return 'One or more properties appear in multiple selected portfolios. Please deselect one of the overlapping portfolios to continue.'
    case ErrorCode.CANNOT_SET_MARKET_RENT_ADJUSTMENT_FOR_UNIT_WITHOUT_MARKET_RENT:
      return 'One or more selected units have no market rent. Market rent adjustment can only be applied to units with market rent. Please deselect units without market rent and try again.'
    case ErrorCode.COMBINE_PROPERTIES_OWNERSHIP_GROUP_MISMATCH:
      return 'You cannot combine properties that belong to different ownership groups. Assign the same ownership group to every property (or clear ownership groups) and try again.'
    case ErrorCode.DUPLICATE_OWNERSHIP_GROUP_NAME:
      return 'An ownership group with this name already exists. Please choose a different name.'
    case ErrorCode.OWNERSHIP_GROUPS_NOT_ENABLED:
      return 'Ownership groups are not enabled for this firm.'
    case ErrorCode.LEASE_UP_INVALID_STABILIZED_OCCUPANCY:
      return 'Stabilized occupancy must be greater than 0% and at most 100%.'
    case ErrorCode.LEASE_UP_INVALID_MONTHLY_GOAL_MONTH_OFFSET:
      return 'Monthly goal month offsets must be non-negative.'
    case ErrorCode.LEASE_UP_INVALID_MONTHLY_GOAL_RATIO:
      return 'Monthly goal leased percentages must be between 0% and 100%.'
    case ErrorCode.LEASE_UP_DUPLICATE_MONTHLY_GOAL_MONTH_OFFSET:
      return 'Monthly goals must have unique month offsets.'
    case ErrorCode.LEASE_UP_MONTHLY_GOALS_NOT_MONOTONIC:
      return 'Cumulative monthly goal values must not decrease as month offset increases.'
    case ErrorCode.LEASE_UP_PLAN_ALREADY_EXISTS:
      return 'A lease-up plan already exists for this property.'
    case ErrorCode.LEASE_UP_PROPERTY_NOT_IN_LEASE_UP_MODE:
      return 'Lease-up plans can only be created for properties in lease-up mode.'
    case ErrorCode.LEASE_UP_PLAN_NOT_EDITABLE:
      return 'This lease-up plan can no longer be edited.'
    case ErrorCode.LEASE_UP_TARGET_DATE_NOT_AFTER_PRELEASE:
      return 'Target stabilization date must be after the prelease start date.'
    case ErrorCode.LEASE_UP_MONTHLY_GOAL_OUT_OF_RANGE:
      return 'Monthly goals must fall within the months between the plan start and target stabilization dates.'
    case ErrorCode.LEASE_UP_INVALID_MINIMUM_LEASED_PERCENT:
      return 'Minimum leased percent must be a whole number between 0% and 25%.'
    case ErrorCode.LEASE_UP_TARGET_DATE_NOT_AFTER_PLAN_START:
      return 'Target stabilization date must be after the plan start date.'
    default:
      exhaustiveSwitchError(errorCode) // no-op, but enforces exhaustive switch
      return null
  }
}
